import { TextInput } from '@fabric-ui/components';
import { INPUT_TYPES } from '../../components/formInputTypes';
import { UploadFileType } from '../../components/UploadFile';
import { isValidEmail } from '../../utils/utils';

export const regionOptions = [
  { title: 'Africa (Cape Town)', value: 'af-south-1' },
  { title: 'Asia Pacific (Hong Kong)', value: 'ap-east-1' },
  { title: 'Asia Pacific (Hyderabad)', value: 'ap-south-2' },
  { title: 'Asia Pacific (Jakarta)', value: 'ap-southeast-3' },
  { title: 'Asia Pacific (Malaysia)', value: 'ap-southeast-5' },
  { title: 'Asia Pacific (Melbourne)', value: 'ap-southeast-4' },
  { title: 'Asia Pacific (Mumbai)', value: 'ap-south-1' },
  { title: 'Asia Pacific (Osaka)', value: 'ap-northeast-3' },
  { title: 'Asia Pacific (Seoul)', value: 'ap-northeast-2' },
  { title: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
  { title: 'Asia Pacific (Sydney)', value: 'ap-southeast-2' },
  { title: 'Asia Pacific (Tokyo)', value: 'ap-northeast-1' },
  { title: 'Canada (Calgary)', value: 'ca-west-1' },
  { title: 'Canada (Central)', value: 'ca-central-1' },
  { title: 'China (Beijing)', value: 'cn-north-1' },
  { title: 'China (Ningxia)', value: 'cn-northwest-1' },
  { title: 'Europe (Frankfurt)', value: 'eu-central-1' },
  { title: 'Europe (Ireland)', value: 'eu-west-1' },
  { title: 'Europe (London)', value: 'eu-west-2' },
  { title: 'Europe (Milan)', value: 'eu-south-1' },
  { title: 'Europe (Paris)', value: 'eu-west-3' },
  { title: 'Europe (Spain)', value: 'eu-south-2' },
  { title: 'Europe (Stockholm)', value: 'eu-north-1' },
  { title: 'Europe (Tel Aviv)', value: 'il-central-1' },
  { title: 'Europe (Zurich)', value: 'eu-central-2' },
  { title: 'Middle East (Bahrain)', value: 'me-south-1' },
  { title: 'Middle East (UAE)', value: 'me-central-1' },
  { title: 'South America (São Paulo)', value: 'sa-east-1' },
  { title: 'US East (N. Virginia)', value: 'us-east-1' },
  { title: 'US East (Ohio)', value: 'us-east-2' },
  { title: 'US West (N. California)', value: 'us-west-1' },
  { title: 'US West (Oregon)', value: 'us-west-2' },
];

const CROWDSTRIKE_BASE_URL_OPTIONS = [
  { title: 'US-1 (Commercial)', value: 'https://api.crowdstrike.com/' },
  { title: 'US-2 (Commercial)', value: 'https://api.us-2.crowdstrike.com/' },
  { title: 'EU-1 (Commercial)', value: 'https://api.eu-1.crowdstrike.com/' },
  { title: 'US-GOV-1 (GovCloud)', value: 'https://api.laggar.gcw.crowdstrike.com/' },
  { title: 'US-GOV-2 (GovCloud)', value: 'https://api.us-gov-2.crowdstrike.mil/' },
];

const CHECKMARX_ONE_URL_OPTIONS = [
  { title: 'US', value: 'https://ast.checkmarx.net' },
  { title: 'US2', value: 'https://us.ast.checkmarx.net' },
  { title: 'EU', value: 'https://eu.ast.checkmarx.net' },
  { title: 'EU2', value: 'https://eu-2.ast.checkmarx.net' },
  { title: 'DEU', value: 'https://deu.ast.checkmarx.net' },
  { title: 'Australia & New Zealand', value: 'https://anz.ast.checkmarx.net' },
  { title: 'India', value: 'https://ind.ast.checkmarx.net' },
  { title: 'Singapore', value: 'https://sng.ast.checkmarx.net' },
  { title: 'UAE', value: 'https://mea.ast.checkmarx.net' },
];

const AUTH_PROVIDER_TYPE_REGIONS = {
  ORCA: [
    { title: 'US Global', value: 'https://api.orcasecurity.io' },
    { title: 'South America', value: 'https://api.sa.orcasecurity.io' },
    { title: 'Europe', value: 'https://app.eu.orcasecurity.io' },
    { title: 'Australia', value: 'https://app.au.orcasecurity.io' },
    { title: 'India', value: 'https://app.in.orcasecurity.io' },
    { title: 'GOV Cloud', value: 'https://app.us.gov.orcasecurity.io' },
  ],
  TENABLE_CNAPP: [
    { title: 'AE', value: 'ae' },
    { title: 'AU', value: 'au' },
    { title: 'BR', value: 'br' },
    { title: 'CA', value: 'ca' },
    { title: 'EU', value: 'eu' },
    { title: 'IN', value: 'in' },
    { title: 'JP', value: 'jp' },
    { title: 'KR', value: 'kr' },
    { title: 'SG', value: 'sg' },
    { title: 'UK', value: 'uk' },
    { title: 'US', value: 'us' },
    { title: 'US2', value: 'us2' },
    { title: 'US GovCloud', value: 'usgov' },
  ],
  SNYK: [
    { title: 'SNYK-US-01', value: 'US-01' },
    { title: 'SNYK-US-02', value: 'US-02' },
    { title: 'SNYK-EU-01', value: 'EU-01' },
    { title: 'SNYK-AU-01', value: 'AU-01' },
  ],
  SONARCLOUD: [
    { title: 'EU', value: 'eu' },
    { title: 'US', value: 'us' },
  ],
};

const SELECT_REGION_PROPS = {
  type: ({ type: Component = TextInput, ...props }) => <Component {...props} />,
  params: newData =>
    newData?.authenticationProviderType in AUTH_PROVIDER_TYPE_REGIONS
      ? {
          options: AUTH_PROVIDER_TYPE_REGIONS[newData.authenticationProviderType],
          type: INPUT_TYPES.SELECT,
        }
      : {},
};

const API_KEY_FIELD = [{ label: 'API Key', valueName: 'apiKey', required: true }];

const USER_NAME_PASSWORDS = [
  { label: 'Username', valueName: 'username', required: true },
  { label: 'Password', valueName: 'password', required: true },
];

export const SSL_VERIFY = [
  { label: 'Verify SSL', valueName: 'verifySSL', type: INPUT_TYPES.BOOL, defaultValue: true },
  { label: 'CA certificate', valueName: 'caCert', params: { rows: 7, multiline: true }, shouldShow: ({ verifySSL }) => verifySSL },
];

const URL_USERNAME_PASSWORDS = [...USER_NAME_PASSWORDS, { label: 'Url', valueName: 'url', required: true }];

const TOKEN = [{ label: 'Token', valueName: 'token', required: true }];

const ORG_ID_TOKEN = [...TOKEN, { label: 'Org Id', valueName: 'orgId', required: false }];

const TENANT_ID_TOKEN = [...TOKEN, { label: 'Tenant Id', valueName: 'tenantId', required: true }];

const API_KEY_URL = [...API_KEY_FIELD, { label: 'Url', valueName: 'url', required: true }];

const REGION_NAMES = [
  {
    label: 'Region Names',
    valueName: 'awsRegionNames',
    required: true,
    type: INPUT_TYPES.SELECT,
    params: {
      isMultiple: true,
      options: regionOptions,
      style: {
        '&.MuiAutocomplete-root.autocomplete-multiple, &.MuiAutocomplete-root.autocomplete-multiple .MuiFormControl-root': {
          maxWidth: '100%',
        },
      },
    },
  },
];

const CLIENT_ID_CLIENT_SECRET = [
  { label: 'Client Id', valueName: 'clientId', required: true },
  { label: 'Client Secret', valueName: 'clientSecret', required: true },
];

const SECRET_KEY_ACCESS_KEY = [
  { label: 'Access Key', valueName: 'accessKey', required: true },
  { label: 'Secret Key', valueName: 'secretKey', required: true },
];

const API_KEY_DOMAIN = [
  { label: 'Api Key', valueName: 'apiKey', required: true },
  { label: 'Domain', valueName: 'domain', required: true },
];
const URL_CLIENT_ID_CLIENT_SECRET = [...CLIENT_ID_CLIENT_SECRET, { label: 'Url', valueName: 'url', required: true }];

const TENANT_ID_CLIENT_ID_CLIENT_SECRET = [...CLIENT_ID_CLIENT_SECRET, { label: 'Tenant Id', valueName: 'tenantId', required: true }];
const DOMAIN_PORT_USER_PASSWORD = [
  { label: 'Domain', valueName: 'domain', required: true },
  { label: 'Port', valueName: 'port', required: true },
  { label: 'User', valueName: 'user', required: true },
  { label: 'Password', valueName: 'password', required: true },
];

const authenticationSourceConfigFields = {
  API_KEY: [...API_KEY_FIELD],
  API_SECRET_API_ID: [
    { label: 'Api Id', valueName: 'apiId', required: true },
    { label: 'Api Secret', valueName: 'apiSecret', required: true },
  ],
  API_KEY_KEY_ID_FQDN: [
    { label: 'Api Key', valueName: 'apiKey', required: true },
    { label: 'Key ID', valueName: 'keyId', required: true },
    { label: 'FQDN', valueName: 'fqdn', required: true },
  ],
  API_KEY_API_SECRET: [...API_KEY_FIELD, { label: 'Api Secret', valueName: 'apiSecret', required: true }],
  API_KEY_DOMAIN_EMAIL: [
    ...API_KEY_FIELD,
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'Email', valueName: 'email', required: true },
  ],
  API_KEY_URL: [...API_KEY_URL],
  USERNAME_PASSWORD: [...USER_NAME_PASSWORDS],
  USERNAME_PASSWORD_SUBSCRIPTION_KEY: [...USER_NAME_PASSWORDS, { label: 'Subscription Key', valueName: 'subscriptionKey', required: true }],
  API_KEY_USERNAME_PASSWORD_COMPANY_ID_CLOUD_NAME: [
    ...USER_NAME_PASSWORDS,
    { label: 'Api Key', valueName: 'apiKey', required: true },
    { label: 'Company Id', valueName: 'companyId', required: true },
    { label: 'Cloud Name', valueName: 'cloudName', required: true },
  ],
  ROLE_ARN: [
    { label: 'Aws Role ARN', valueName: 'awsRoleArn', required: true },
    { label: 'External ID', valueName: 'externalId', required: true },
    ...REGION_NAMES,
  ],
  AWS_ANY_SOURCE: [
    { label: 'Aws Role ARN', valueName: 'awsRoleArn', required: true },
    { label: 'External ID', valueName: 'externalId', required: true },
    {
      label: 'Region Name',
      valueName: 'awsRegionName',
      required: true,
      type: INPUT_TYPES.SELECT,
      params: {
        options: regionOptions,
        style: {
          '&.MuiAutocomplete-root.autocomplete-multiple, &.MuiAutocomplete-root.autocomplete-multiple .MuiFormControl-root': {
            maxWidth: '100%',
          },
        },
      },
    },
  ],
  AZURE_CONNECTION: [
    { label: 'Azure Blob Storage Account Connection', valueName: 'azureBlobStorageAccountConnectionString', required: true },
  ],
  CLIENT_ID_CLIENT_SECRET: [...CLIENT_ID_CLIENT_SECRET],
  CLIENT_ID_CLIENT_SECRET_FDR_CLIENT_SECRET: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'FDR Client Secret', valueName: 'fdrClientSecret', required: true },
  ],
  TOKEN: [...TOKEN],
  PERSONAL_TOKEN: [{ label: 'Personal Token', valueName: 'personalToken', required: true }],
  REFRESH_TOKEN_CLIENT_ID_CLIENT_SECRET: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'Refresh Token', valueName: 'refreshToken', required: true },
  ],
  REGION_TOKEN: [
    ...TOKEN,
    {
      label: 'Region',
      valueName: 'region',
      required: true,
      ...SELECT_REGION_PROPS,
    },
  ],
  SECRET_KEY_ACCESS_KEY: [...SECRET_KEY_ACCESS_KEY],
  TENANT_ID_CLIENT_ID_CLIENT_SECRET: [...TENANT_ID_CLIENT_ID_CLIENT_SECRET],
  TENANT_ID_TOKEN: [...TENANT_ID_TOKEN],
  TENANT_TOKEN_URL: [...TENANT_ID_TOKEN, { label: 'Token Url', valueName: 'tokenUrl', required: true }],
  URL_CLIENT_ID_CLIENT_SECRET: [...URL_CLIENT_ID_CLIENT_SECRET],
  CLIENT_ID_CLIENT_SECRET_CROWDSTRIKE_URL: [
    ...CLIENT_ID_CLIENT_SECRET,
    {
      label: 'Region / Custom Base URL',
      valueName: 'url',
      required: true,
      defaultValue: 'https://api.crowdstrike.com/',
      type: INPUT_TYPES.OPEN_SELECT,
      params: {
        options: CROWDSTRIKE_BASE_URL_OPTIONS,
        isMultiple: false,
      },
      tooltip: 'Select your CrowdStrike cloud region, or type a custom base URL.',
    },
  ],
  TOKEN_URL_CLIENT_ID_CLIENT_SECRET_URL: [...URL_CLIENT_ID_CLIENT_SECRET, { label: 'Token Url', valueName: 'tokenUrl', required: true }],
  URL_USERNAME_PASSWORDS: [...URL_USERNAME_PASSWORDS],
  USER_ID_API_TOKEN: [
    { label: 'User Id', valueName: 'userId', required: true },
    { label: 'Api Token', valueName: 'apiToken', required: true },
  ],
  USERNAME_PASSWORD_PCE_ORGID: [
    { label: 'Username', valueName: 'username', required: true },
    { label: 'Password', valueName: 'password', required: true },
    { label: 'PCE', valueName: 'pce', required: true },
    { label: 'Org Id', valueName: 'orgId', required: true },
  ],
  USERNAME_PASSWORD_HOST_DB_NAME: [
    { label: 'Username', valueName: 'username', required: true },
    { label: 'Password', valueName: 'password', required: true },
    { label: 'Host', valueName: 'host', required: true },
    { label: 'DB Name', valueName: 'dbName', required: true },
  ],
  DOMAIN_SECRET_KEY_API_KEY: [
    { label: 'Api Key', valueName: 'apiKey', required: true },
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'Secret Key', valueName: 'secretKey', required: true },
  ],
  API_KEY_DOMAIN: [...API_KEY_DOMAIN],
  TOKEN_URL: [...TOKEN, { label: 'Url', valueName: 'url', required: true }],
  ORG_ID_TOKEN: [...ORG_ID_TOKEN],
  ORG_ID_TOKEN_REGION: [...ORG_ID_TOKEN, { label: 'Region', valueName: 'region', required: true, ...SELECT_REGION_PROPS }],
  API_KEY_API_SECRET_ACCOUNT_ID: [
    ...API_KEY_FIELD,
    { label: 'Api Secret', valueName: 'apiSecret', required: true },
    { label: 'Account Id', valueName: 'accountId', required: true },
  ],
  API_KEY_API_SECRET_URL: [
    ...API_KEY_FIELD,
    { label: 'Api Secret', valueName: 'apiSecret', required: true },
    { label: 'URL', valueName: 'url', required: true },
  ],
  SECRET_KEY_ACCESS_KEY_URL: [...SECRET_KEY_ACCESS_KEY, { label: 'Url', valueName: 'url', required: true }],
  API_KEY_URL_APPLICATION_ID: [...API_KEY_URL, { label: 'Application Id', valueName: 'applicationId', required: true }],
  SERVER_URL_TOKEN: [...TOKEN, { label: 'Server Url', valueName: 'serverUrl', required: true }],
  API_KEY_REGION: [...API_KEY_FIELD, { label: 'Region', valueName: 'region', required: true }],
  SECRET_KEY_ACCESS_KEY_SERVER_URL: [...SECRET_KEY_ACCESS_KEY, { label: 'Server Url', valueName: 'serverUrl', required: true }],
  API_KEY_TENANT: [...API_KEY_FIELD, { label: 'Tenant', valueName: 'tenant', required: true }],
  TOKEN_ORGANIZATION: [...TOKEN, { label: 'Organization', valueName: 'organization', required: true }],
  TOKEN_ORGANIZATION_DOMAIN: [
    ...TOKEN,
    { label: 'Organization', valueName: 'organization', required: true },
    { label: 'Domain', valueName: 'domain', required: true, defaultValue: 'eu', ...SELECT_REGION_PROPS },
  ],
  CLIENT_ID_CLIENT_SECRET_ORGANIZATION_ID: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'Organization Id', valueName: 'organizationId', required: true },
  ],
  TOKEN_HOSTNAME: [...TOKEN, { label: 'Hostname', valueName: 'host', required: true }],
  CLIENT_ID_CLIENT_SECRET_REGION: [...CLIENT_ID_CLIENT_SECRET, ...REGION_NAMES],
  TOKEN_DOMAIN_EMAIL: [
    ...TOKEN,
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'Email', valueName: 'email', required: true },
  ],
  USERNAME_PASSWORD_HOST: [...USER_NAME_PASSWORDS, { label: 'Host', valueName: 'host', required: true }],
  SERVICE_ACCOUNT: [
    {
      label: 'Service Account',
      valueName: 'serviceAccountUploadedFile',
      type: INPUT_TYPES.UPLOAD_FILE,
      params: newData => ({
        fileType: UploadFileType.Config,
        url: 'data-source-instance/uploadConfigFile',
        filename: 'multipartFile',
        isUploadConfig: true,
        lastUploadedFileName: newData.config.uploadedFileName,
      }),
      required: true,
    },
    {
      label: 'Service Account File Content Holder',
      valueName: 'uploadedFileContent',
      required: true,
      shouldShow: () => false,
    },
    {
      label: 'Service Account File Name Holder',
      valueName: 'uploadedFileName',
      required: true,
      shouldShow: () => false,
    },
    { label: 'Admin Email to Impersonate', valueName: 'email', required: false },
  ],
  GITHUB_APP_TOKEN: [
    {
      label: 'Private Key (PEM file)',
      valueName: 'privateKeyUploadedFile',
      type: INPUT_TYPES.UPLOAD_FILE,
      params: newData => ({
        fileType: UploadFileType.Config,
        url: 'data-source-instance/uploadConfigFile',
        filename: 'multipartFile',
        isUploadConfig: true,
        lastUploadedFileName: newData.config.uploadedFileName,
      }),
      required: true,
    },
    {
      label: 'Service Account File Content Holder',
      valueName: 'uploadedFileContent',
      required: true,
      shouldShow: () => false,
    },
    {
      label: 'Service Account File Name Holder',
      valueName: 'uploadedFileName',
      required: true,
      shouldShow: () => false,
    },
    {
      label: 'App ID',
      valueName: 'appId',
      required: true,
    },
    {
      label: 'Installation ID',
      valueName: 'installationId',
      required: true,
    },
    {
      label: 'Organization',
      valueName: 'organization',
      required: true,
    },
  ],
  MTLS: [
    {
      label: 'Private Key (PEM)',
      valueName: 'clientKey',
      params: { rows: 7, multiline: true },
      required: true,
    },
    {
      label: 'Key Password',
      valueName: 'clientKeyPassword',
      required: false,
    },
    {
      label: 'Certificate Chain (PEM)',
      valueName: 'clientCertificate',
      params: { rows: 7, multiline: true },
      required: true,
    },
    {
      label: 'CA PEM Certificate',
      valueName: 'caCert',
      params: { rows: 7, multiline: true },
      required: false,
    },
  ],
  USERNAME_PASSWORD_CLIENT_ID_CLIENT_SECRET: [...USER_NAME_PASSWORDS, ...CLIENT_ID_CLIENT_SECRET],
  URL_TENANT_ID_CLIENT_ID_CLIENT_SECRET: [
    ...TENANT_ID_CLIENT_ID_CLIENT_SECRET,
    {
      label: 'Url',
      valueName: 'url',
      required: true,
    },
  ],
  CLIENT_ID_CLIENT_SECRET_TENANT_ID_CHECKMARX_ONE_URL: [
    ...TENANT_ID_CLIENT_ID_CLIENT_SECRET,
    {
      label: 'Region / Custom URL',
      valueName: 'url',
      required: true,
      defaultValue: CHECKMARX_ONE_URL_OPTIONS[0].value,
      type: INPUT_TYPES.OPEN_SELECT,
      params: {
        options: CHECKMARX_ONE_URL_OPTIONS,
        isMultiple: false,
      },
      tooltip: 'Select your Checkmarx One region, or type a custom instance URL.',
    },
  ],
  USERNAME_PASSWORD_TENANT: [...USER_NAME_PASSWORDS, { label: 'Tenant', valueName: 'tenant', required: true }],
  EMAIL_USER_KEY_ORG_UUID: [
    { label: 'Email', valueName: 'email', required: true },
    { label: 'User Key', valueName: 'userKey', required: true },
    { label: 'Organization UUID', valueName: 'orgUuid', required: true },
  ],
  USERNAME_PRIVATE_KEY_PASSPHRASE: [
    { label: 'User Name', valueName: 'username', required: true },
    { label: 'Private Key', valueName: 'privateKey', required: true },
    { label: 'Passphrase', valueName: 'passphrase', required: true },
  ],
  CREDENTIALS_EMAIL: [
    { label: 'Credentials Json', valueName: 'credentialsJson', required: true },
    { label: 'Email', valueName: 'email', required: true },
  ],
  MULTIPLE_ACCOUNT: [
    { label: 'Role ARNs List', valueName: 'roleArnsList', required: true },
    { label: 'External ID', valueName: 'externalId', required: true },
    {
      label: 'Region Names',
      valueName: 'awsRegionNames',
      required: true,
      type: INPUT_TYPES.SELECT,
      params: {
        isMultiple: true,
        options: regionOptions,
        style: {
          '&.MuiAutocomplete-root.autocomplete-multiple, &.MuiAutocomplete-root.autocomplete-multiple .MuiFormControl-root': {
            maxWidth: '100%',
          },
        },
      },
    },
  ],
  OAUTH2_AUTHENTICATION_SOURCE_CONFIG: [
    { label: 'Client Id', valueName: 'oauthClientId', required: true },
    { label: 'Client Secret', valueName: 'oauthClientSecret', required: true },
    { label: 'Refresh Token', valueName: 'oauthRefreshToken', required: false },
    { label: 'Access Token', valueName: 'oauthAccessToken', required: false },
    { label: 'Tenant Id', valueName: 'oauthTenantId', required: false },
  ],
  OAUTH2: [
    { label: 'Client Id', valueName: 'clientId', required: true },
    { label: 'Client Secret', valueName: 'clientSecret', required: true },
    { label: 'Refresh Token', valueName: 'refreshToken', required: true },
    { label: 'Access Token', valueName: 'accessToken', required: false },
    { label: 'Tenant Id', valueName: 'tenantId', required: true },
  ],
  GENERIC_OAUTH2: [
    { label: 'Client Id', valueName: 'clientId', required: true },
    { label: 'Client Secret', valueName: 'clientSecret', required: true },
    { label: 'Refresh Token', valueName: 'refreshToken', required: true },
    { label: 'Access Token', valueName: 'accessToken', required: false },
    { label: 'Authentication Url', valueName: 'tokenUrl', required: true },
  ],
  GOOGLE_OAUTH2: [
    { label: 'Client Id', valueName: 'clientId', required: true },
    { label: 'Client Secret', valueName: 'clientSecret', required: true },
    { label: 'Refresh Token', valueName: 'refreshToken', required: true },
    { label: 'Access Token', valueName: 'accessToken', required: false },
    { label: 'Redirect Url', valueName: 'redirectUri', required: false },
    { label: 'Token Url', valueName: 'tokenUrl', required: true, defaultValue: 'https://oauth2.googleapis.com/token' },
  ],
  DOMNAIN_URL_TOKEN: [
    { label: 'Token', valueName: 'token', required: true },
    { label: 'Url', valueName: 'url', required: true },
    { label: 'Domain', valueName: 'domain', required: true },
  ],
  CLIENT_ID_CLIENT_SECRET_BASE_URL: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'Authentication Service Base URL', valueName: 'authenticationServiceBaseUrl', required: true },
  ],
  OKTA_OAUTH2: [
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'Client Id', valueName: 'client_id', required: true },
    { label: 'Private Key', valueName: 'private_key', required: true, params: { rows: 7, multiline: true } },
    { label: 'Key Id', valueName: 'key_id', required: true },
  ],
  JWT: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'Public Key ID', valueName: 'publicKeyId', required: true },
    { label: 'Private Key', valueName: 'privateKey', required: true },
    { label: 'Passphrase', valueName: 'passphrase', required: false },
    { label: 'Enterprise ID', valueName: 'enterpriseId', required: false },
  ],
  JWT_OAUTH2: [
    ...CLIENT_ID_CLIENT_SECRET,
    { label: 'Key ID', valueName: 'keyId', required: true },
    { label: 'subject', valueName: 'subject', required: true },
  ],
  DOMAIN_EMAIL_PASSWORD: [
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'Email', valueName: 'email', required: true },
    { label: 'Password (API Token)', valueName: 'password', required: true },
  ],
  HEADER_NAME_HEADER_VALUE: [
    { label: 'Auth Header Name', valueName: 'headerName', required: true },
    { label: 'Auth Header Value', valueName: 'headerValue', required: true },
  ],
  DOMAIN_PORT_USER_PASSWORD: [...DOMAIN_PORT_USER_PASSWORD],
  HOST_DOMAIN_PORT_USER_PASSWORD: [...DOMAIN_PORT_USER_PASSWORD, { label: 'Host', valueName: 'host', required: true }],
  DOMAIN_USERTOKEN_USEREMAIL_SECRET: [
    { label: 'Domain', valueName: 'domain', required: true },
    { label: 'User Token', valueName: 'userToken', required: false },
    { label: 'User Email', valueName: 'userEmail', required: false },
    { label: 'Secret', valueName: 'secret', required: false },
  ],
  URL_USERNAME_PASSWORD_GENERATED_JWT: URL_USERNAME_PASSWORDS,
  TENANT_ID_ADMIN_EMAIL: [
    { label: 'Tenant Id', valueName: 'tenantId', required: true, disabledOnEdit: true },
    {
      label: 'MDE Admin Email',
      valueName: 'adminEmail',
      required: true,
      tooltip:
        'Enter the email address of the Microsoft Admin who will provision this integration in MDE. This user will need to have permissions for data export from the security console.',
      validate: (value: string) => (value && !isValidEmail(value) ? 'Please enter a valid email address' : undefined),
    },
  ],
};

export default authenticationSourceConfigFields;
